import { /*Map,*/ fromJS } from "immutable";
import { handleActions, createAction } from "redux-actions";
import Restful from "lib/restful";
import { replaceSpaceWithHyphen } from "lib/textFilter";
import GaHelper from "lib/gaHelper";
import config from "lib/fr-config";
import { sleep } from "lib/commonUtil";

const UNIT_LENGTH = config.outfitList.LENGTH;
const EXP_MINUTE = 30;
const WAIT_MILLISEC = config.storeModule.waitForLoadingMilliSec;

const SET_IS_LOADING = "search/SET_IS_LOADING";
const SET_SEARCH_OPTION = "search/SET_SEARCH_OPTION";
const SET_EXPIRATION_DATE = "search/SET_EXPIRATION_DATE";
const INIT_TARGET_DATA = "search/INIT_TARGET_DATA";
const SET_OUTFIT_LIST = "search/SET_OUTFIT_LIST";
const CHECK_IS_END = "search/CHECK_IS_END";
const EXPORT_TARGET_DATA = "search/EXPORT_TARGET_DATA";
const SET_NEED_CREATIVE = "search/SET_NEED_CREATIVE";
const INIT_EXPORTED_DATA = "search/INIT_EXPORTED_DATA";
const INIT_SELECTED_SEARCH_VALUE = "search/INIT_SELECTED_SEARCH_VALUE";
const INIT_ALL_DATA = "search/INIT_ALL_DATA";

const setIsLoading = createAction(SET_IS_LOADING);
const setSearchOption = createAction(SET_SEARCH_OPTION);
const setExpirationDate = createAction(SET_EXPIRATION_DATE);
const initTargetData = createAction(INIT_TARGET_DATA);
const setOutfitList = createAction(SET_OUTFIT_LIST);
const checkIsEnd = createAction(CHECK_IS_END);
const exportTargetData = createAction(EXPORT_TARGET_DATA);
const setNeedCreative = createAction(SET_NEED_CREATIVE);
const initExportedData = createAction(INIT_EXPORTED_DATA);
const initSelectedSearchValue = createAction(INIT_SELECTED_SEARCH_VALUE);
const initAllData = createAction(INIT_ALL_DATA);

const getAdditionalType = input => {
  const { brand, color, tag } = input;
  const additionalType = !!brand
    ? "brand"
    : !!color
    ? "color"
    : !!tag
    ? "tag"
    : "nullType";
  const additionalValue =
    additionalType === "nullType" ? "nullValue" : input[additionalType];
  return { additionalType, additionalValue };
};

const getSearchState = ({ getState }) => {
  const searchState = getState().search.toJS();
  return searchState;
};

const getSerachOption = ({ getState }) => {
  const searchState = getSearchState({ getState });
  const { category, gender, brand, color, tag } = searchState;
  return { category, gender, brand, color, tag };
};

const getIsLoading = ({ getState }) => {
  const searchState = getSearchState({ getState });
  const { isLoading } = searchState;
  return isLoading;
};

const getLastHash = list => {
  if (list.length === 0) return undefined;
  const length = list.length;
  return list[length - 1]["outfitHash"];
};

const getTargetData = ({ getState, searchOption }) => {
  const { category, gender, brand, color, tag } = searchOption;
  const searchState = getSearchState({ getState });
  const { data } = searchState;
  const { additionalType, additionalValue } = getAdditionalType({
    brand,
    color,
    tag
  });
  const targetData =
    data &&
    data[category] &&
    data[category][additionalType] &&
    data[category][additionalType][additionalValue] &&
    data[category][additionalType][additionalValue][gender];
  return targetData;
};

const isSameSearchOption = (optionA, optionB) => {
  const {
    category: categoryA,
    gender: genderA,
    brand: brandA,
    color: colorA,
    tag: tagA
  } = optionA;
  const {
    category: categoryB,
    gender: genderB,
    brand: brandB,
    color: colorB,
    tag: tagB
  } = optionB;
  if (
    categoryA === categoryB &&
    genderA === genderB &&
    brandA === brandB &&
    colorA === colorB &&
    tagA === tagB
  ) {
    return true;
  } else {
    return false;
  }
};

const checkExpDate = ({ getState, searchOption }) => {
  const targetData = getTargetData({ getState, searchOption });
  const expirationDate = targetData && targetData.expirationDate;
  if (!expirationDate) return { needUpdate: true };
  const nowDate = new Date();
  return { needUpdate: nowDate > expirationDate ? true : false };
};

const setExpDate = ({ dispatch, searchOption }) => {
  const expirationDate = new Date();
  expirationDate.setMinutes(expirationDate.getMinutes() + EXP_MINUTE);
  dispatch(setExpirationDate({ searchOption, expirationDate }));
};

const addNewList = async ({ idToken, dispatch, getState, searchOption }) => {
  const { category, gender, brand, color, tag } = searchOption;
  const targetData = getTargetData({ getState, searchOption });
  const { savedList } = targetData;
  const length = UNIT_LENGTH;
  const lastHash = getLastHash(savedList);
  const { additionalType, additionalValue } = getAdditionalType({
    brand,
    color,
    tag
  });
  const queryObj = { category, gender };
  switch (additionalType) {
    case "brand":
      queryObj[additionalType] = replaceSpaceWithHyphen(additionalValue);
      break;
    case "category":
    case "color":
    case "tag":
      queryObj[additionalType] = additionalValue;
      break;
    case "nullType":
    default:
      break;
  }
  const input = { idToken, queryObj, lastHash, length };

  const { code, res, errInfo } = await Restful.getSearchedOutfitAPI(input);
  if (code === "error") {
    const errObj = Error("api error");
    errObj.errorValue = { ...errInfo };
    throw errObj;
  }

  const { list, needCreative } = res;
  dispatch(setNeedCreative(!!needCreative));

  dispatch(setOutfitList({ searchOption, savedList: savedList.concat(list) }));
  dispatch(checkIsEnd({ searchOption, length: list.length }));
  GaHelper.setGARequestOutfitListEvent({
    type: "searchOutfitList",
    pageNum: savedList.length / UNIT_LENGTH + 1,
    detailObj: { ...queryObj }
  });

  return { needCreative: !!needCreative };
};

export const changeListType =
  ({ idToken, category, brand, color, tag, gender }) =>
  async (dispatch, getState) => {
    if (!category || !gender) {
      throw Error(`input type error => category:${category}, gender:${gender}`);
    }

    let isLoading = getIsLoading({ getState });

    while (isLoading) {
      await sleep(WAIT_MILLISEC);
      isLoading = getIsLoading({ getState });
    }

    const searchOption = { category, brand, color, tag, gender };

    dispatch(setIsLoading(true));
    dispatch(setSearchOption(searchOption));

    const { needUpdate } = checkExpDate({ getState, searchOption });
    let needMembership = false;
    if (needUpdate) {
      dispatch(initExportedData());
      dispatch(initTargetData(searchOption));
      try {
        const { needCreative } = await addNewList({
          idToken,
          dispatch,
          getState,
          searchOption
        });
        needMembership = needCreative;
      } catch (error) {
        dispatch(setIsLoading(false));
        throw error;
      }
    }

    const nowSearchOption = getSerachOption({ getState });
    if (isSameSearchOption(searchOption, nowSearchOption)) {
      dispatch(exportTargetData(searchOption));
    }

    if (needUpdate) {
      if (!needMembership) {
        setExpDate({ dispatch, searchOption });
      }
      GaHelper.setGASearchEvent(searchOption);
    }

    dispatch(setIsLoading(false));
  };

export const getList =
  ({ idToken }) =>
  async (dispatch, getState) => {
    const searchState = getSearchState({ getState });
    const { isEnd } = searchState;
    const isLoading = getIsLoading({ getState });

    if (isLoading || isEnd) return null;

    dispatch(setIsLoading(true));

    const searchOption = getSerachOption({ getState });

    try {
      await addNewList({ idToken, dispatch, getState, searchOption });
    } catch (error) {
      dispatch(setIsLoading(false));
      throw error;
    }

    const nowSearchOption = getSerachOption({ getState });
    if (isSameSearchOption(searchOption, nowSearchOption)) {
      dispatch(exportTargetData(searchOption));
    }

    dispatch(setIsLoading(false));
  };

export const clearExportedSearchData = () => (dispatch, getState) => {
  dispatch(initExportedData());
  dispatch(initSelectedSearchValue());
};

export const deleteAllData = () => (dispatch, getState) => {
  dispatch(initAllData());
};

const initialState = fromJS({
  data: {},
  /*
  data 의 구조.
  data: {
    [category]: {
      [brand || color || tag || "nullType"]: {
        [value || "nullValue"]: {
          [gender]: {
            savedList: [],
            expirationDate: null,
            isEnd: false
          }
        }
      }
    }
  },
  */
  category: null,
  brand: null,
  color: null,
  tag: null,
  gender: null,

  outfitList: [],
  isEnd: false,
  isLoading: false,

  needCreative: false
});

export default handleActions(
  {
    [SET_IS_LOADING]: (state, action) => {
      const isLoading = action.payload;
      return state.set("isLoading", fromJS(isLoading));
    },
    [SET_SEARCH_OPTION]: (state, action) => {
      const { category, brand, color, tag, gender } = action.payload;
      return state
        .set("category", fromJS(category))
        .set("tag", fromJS(tag))
        .set("color", fromJS(color))
        .set("brand", fromJS(brand))
        .set("gender", fromJS(gender));
    },
    [SET_EXPIRATION_DATE]: (state, action) => {
      const { searchOption, expirationDate } = action.payload;
      const { category, gender, brand, color, tag } = searchOption;
      const { additionalType, additionalValue } = getAdditionalType({
        brand,
        color,
        tag
      });
      return state.setIn(
        [
          "data",
          category,
          additionalType,
          additionalValue,
          gender,
          "expirationDate"
        ],
        fromJS(expirationDate)
      );
    },
    [INIT_TARGET_DATA]: (state, action) => {
      const { category, gender, brand, color, tag } = action.payload;
      const { additionalType, additionalValue } = getAdditionalType({
        brand,
        color,
        tag
      });
      const newData = {
        savedList: [],
        expirationDate: null,
        isEnd: false
      };
      return state.setIn(
        ["data", category, additionalType, additionalValue, gender],
        fromJS(newData)
      );
    },
    [SET_OUTFIT_LIST]: (state, action) => {
      const { searchOption, savedList } = action.payload;
      const { category, gender, brand, color, tag } = searchOption;
      const { additionalType, additionalValue } = getAdditionalType({
        brand,
        color,
        tag
      });
      return state.setIn(
        [
          "data",
          category,
          additionalType,
          additionalValue,
          gender,
          "savedList"
        ],
        fromJS(savedList)
      );
    },
    [CHECK_IS_END]: (state, action) => {
      const { searchOption, length } = action.payload;
      const { category, gender, brand, color, tag } = searchOption;
      const { additionalType, additionalValue } = getAdditionalType({
        brand,
        color,
        tag
      });
      const isEnd = length === UNIT_LENGTH ? false : true;
      return state.setIn(
        ["data", category, additionalType, additionalValue, gender, "isEnd"],
        fromJS(isEnd)
      );
    },
    [EXPORT_TARGET_DATA]: (state, action) => {
      const { category, gender, brand, color, tag } = action.payload;
      const { data } = state.toJS();
      const { additionalType, additionalValue } = getAdditionalType({
        brand,
        color,
        tag
      });
      const { savedList, isEnd } =
        data[category][additionalType][additionalValue][gender];
      return state
        .set("outfitList", fromJS(savedList))
        .set("isEnd", fromJS(isEnd));
    },
    [SET_NEED_CREATIVE]: (state, action) => {
      const needCreative = action.payload;
      return state.set("needCreative", fromJS(needCreative));
    },
    [INIT_EXPORTED_DATA]: (state, action) => {
      const { needCreative, outfitList, isEnd } = initialState.toJS();
      return state
        .set("needCreative", fromJS(needCreative))
        .set("outfitList", fromJS(outfitList))
        .set("isEnd", fromJS(isEnd));
    },
    [INIT_SELECTED_SEARCH_VALUE]: (state, action) => {
      const { category, brand, color, tag, gender } = initialState.toJS();
      return state
        .set("category", fromJS(category))
        .set("brand", fromJS(brand))
        .set("color", fromJS(color))
        .set("tag", fromJS(tag))
        .set("gender", fromJS(gender));
    },
    [INIT_ALL_DATA]: (state, action) => {
      return initialState;
    }
  },
  initialState
);
